"use client";

import { useEffect } from "react";

/**
 * Records clicks on tracked CTAs across the public site. Any element with a
 * `data-track="<name>"` attribute (or a descendant of one) reports a click to
 * /api/track. Uses a single capture-phase listener so it fires before any
 * navigation, and sendBeacon so the request survives the page unload.
 *
 * The same listener fires the Meta Pixel's standard `Contact` event on any
 * phone (`tel:`) or LINE link — per marketing, a call/LINE tap counts as a
 * contact conversion. `window.fbq` is not installed by this site: it comes
 * with the GTM container's Meta Pixel tag, which is itself loaded only after
 * the visitor has accepted (lib/analytics/load-tags.ts). So the guard below is
 * still the consent check, and it is also what makes this safe if marketing
 * ever pauses the tag in GTM — the event is skipped, not thrown.
 *
 * Mounted once in the site root layout.
 */
export default function ClickTracker() {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const start = e.target as HTMLElement | null;

      const link = start?.closest<HTMLAnchorElement>("a[href]");
      if (link && typeof window.fbq === "function") {
        const href = link.getAttribute("href") ?? "";
        const isPhone = href.startsWith("tel:");
        const isLine = href.includes("lin.ee/") || href.includes("line.me/");
        if (isPhone || isLine) {
          window.fbq("track", "Contact");
        }
      }

      const el = start?.closest<HTMLElement>("[data-track]");
      const name = el?.dataset.track?.trim();
      if (!name) return;

      const payload = JSON.stringify({ name, path: window.location.pathname });

      try {
        const blob = new Blob([payload], { type: "application/json" });
        if (navigator.sendBeacon?.("/api/track", blob)) return;
      } catch {
        // fall through to fetch
      }

      fetch("/api/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: payload,
      }).catch(() => {});
    }

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}
